import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { 
    FaHome, 
    FaList, 
    FaBoxOpen, 
    FaShoppingCart, 
    FaUsers, 
    FaCog, 
    FaChartBar,
    FaSignOutAlt,
    FaLanguage
} from 'react-icons/fa';

const Sidebar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t, isRTL, toggleLanguage } = useTranslation();
    const { logout, user } = useAuth();

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    // Create menu items based on user permissions
    const menuItems = useMemo(() => {
        
        // Base menu items that everyone can see
        const items = [
            { path: '/admin', icon: FaHome, label: 'admin.dashboard' },
            { path: '/admin/categories', icon: FaList, label: 'admin.categories' },
            { path: '/admin/items', icon: FaBoxOpen, label: 'admin.items' },
            { path: '/admin/orders', icon: FaShoppingCart, label: 'admin.orders' },
            { path: '/admin/settings', icon: FaCog, label: 'admin.settings' },
            { path: '/admin/reports', icon: FaChartBar, label: 'admin.reports' },
        ];
        
        // Only show Users section if user doesn't have is_one_checked flag
        if (!user?.is_one_checked) {
            // Insert Users menu item after Orders
            items.splice(4, 0, { path: '/admin/users', icon: FaUsers, label: 'admin.users' });
        }
        
        return items;
    }, [user]);

    const isActive = (path) => {
        return location.pathname === path;
    };

    const MenuLink = ({ to, icon: Icon, label, active }) => (
        <Link
            to={to}
            className={`block p-3 rounded-lg transition-colors duration-200 w-full
                ${active ? 'bg-yellow-500 text-white' : 'text-gray-300 hover:bg-gray-700'}`
            }
        >
            <div className={`flex items-center gap-3 w-full ${isRTL ? 'justify-end' : ''}`} style={{ direction: 'ltr' }}>
                <Icon className={`w-5 h-5 flex-shrink-0 ${isRTL ? 'order-last' : 'order-first'}`} />
                <span>{t(label)}</span>
            </div>
        </Link>
    );

    return (
        <div className="h-full bg-gray-800 text-white w-64">
            <div className="p-4">
                <h1 className={`text-2xl font-bold mb-8 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('admin.restaurantName')}
                </h1>
                <nav>
                    <ul className="space-y-2">
                        {menuItems.map((item) => (
                            <li key={item.path}>
                                <MenuLink
                                    to={item.path}
                                    icon={item.icon}
                                    label={item.label}
                                    active={isActive(item.path)}
                                />
                            </li>
                        ))}
                    </ul>
                </nav>
            </div>
            
            {/* Bottom Actions */}
            <div className="absolute bottom-0 w-full p-4 border-t border-gray-700">
                <button
                    onClick={toggleLanguage}
                    className="block w-full p-3 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors duration-200"
                >
                    <div className={`flex items-center gap-3 w-full ${isRTL ? 'justify-end' : ''}`} style={{ direction: 'ltr' }}>
                        <FaLanguage className={`w-5 h-5 flex-shrink-0 ${isRTL ? 'order-last' : 'order-first'}`} />
                        <span>{t('admin.changeLanguage')}</span>
                    </div>
                </button>
                <button
                    onClick={handleLogout}
                    className="block w-full p-3 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors duration-200 mt-2"
                >
                    <div className={`flex items-center gap-3 w-full ${isRTL ? 'justify-end' : ''}`} style={{ direction: 'ltr' }}>
                        <FaSignOutAlt className={`w-5 h-5 flex-shrink-0 ${isRTL ? 'order-last' : 'order-first'}`} />
                        <span>{t('admin.logout')}</span>
                    </div>
                </button>
            </div>
        </div>
    );
};

export default Sidebar;
